import { useMemo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';

export type LegendItem = {
  value: string | number;
  color: string;
  label: string;
  payload?: Record<string, unknown>;
};

export type SmartLegendProps = {
  /** Array of legend items to display */
  payload?: LegendItem[];
  /** Maximum number of items to show before limiting */
  maxVisibleItems?: number;
  /** Sort order: 'value' sorts by numeric value, 'label' sorts alphabetically */
  sortBy?: 'value' | 'label' | 'none';
  /** Custom function to extract sort value from item */
  sortExtractor?: (item: LegendItem) => number;
  /** Layout direction */
  layout?: 'horizontal' | 'vertical';
  /** Custom wrapper class name */
  className?: string;
};

/**
 * SmartLegend - Intelligent legend component that limits displayed items
 * and shows 'etc...' when there are more items than maxVisibleItems
 */
export function SmartLegend({
  payload = [],
  maxVisibleItems = 10,
  sortBy = 'none',
  sortExtractor,
  layout = 'horizontal',
  className = '',
}: SmartLegendProps) {
  const { t } = useTranslation();

  // Sort and filter legend items
  const sortedItems = useMemo(() => {
    if (!payload || payload.length === 0) return [];

    const sorted = [...payload];

    if (sortBy === 'value' && sortExtractor) {
      // Sort by custom extractor
      return sorted.sort((a, b) => {
        const aVal = sortExtractor(a);
        const bVal = sortExtractor(b);
        return bVal - aVal; // Descending order
      });
    } else if (sortBy === 'label') {
      // Sort alphabetically by label
      return sorted.sort((a, b) => {
        const aLabel = String(a.label || '').toLowerCase();
        const bLabel = String(b.label || '').toLowerCase();
        return aLabel.localeCompare(bLabel);
      });
    } else if (sortBy === 'value') {
      // Sort by value property (if numeric)
      return sorted.sort((a, b) => {
        const aVal = typeof a.value === 'number' ? a.value : 0;
        const bVal = typeof b.value === 'number' ? b.value : 0;
        return bVal - aVal; // Descending order
      });
    }

    return sorted;
  }, [payload, sortBy, sortExtractor]);

  // Determine visible items - show all if <= maxVisibleItems, otherwise limit
  const visibleItems = useMemo(() => {
    if (sortedItems.length <= maxVisibleItems) {
      return sortedItems;
    }
    return sortedItems.slice(0, maxVisibleItems);
  }, [sortedItems, maxVisibleItems]);

  // Check if we need to show "etc..."
  const hasMoreItems = sortedItems.length > maxVisibleItems;

  if (!payload || payload.length === 0) {
    return null;
  }

  const layoutClass = layout === 'vertical' ? 'flex-col items-start' : 'flex-row flex-wrap';

  return (
    <div
      className={`flex ${layout === 'vertical' ? 'flex-col' : 'flex-row'} items-center gap-2 ${className}`}
    >
      {/* Legend Items */}
      <div className={`flex ${layoutClass} justify-center gap-4`}>
        {visibleItems.map((entry, index) => (
          <div key={`legend-${index}`} className="flex min-w-0 items-center gap-2">
            <div
              className="h-2 w-2 shrink-0 rounded-sm"
              style={{ backgroundColor: entry.color }}
              aria-hidden="true"
            />
            <span className="max-w-[9rem] truncate text-xs text-foreground" title={entry.label}>
              {entry.label}
            </span>
          </div>
        ))}
        {/* Overflow marker when there are more items than maxVisibleItems */}
        {hasMoreItems && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground italic">
              {t('analytics.dashboard.complexCharts.legend.more')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
